// The in-memory API's Chief of Staff modes (COS-MODES; docs/design/2026-10-30-chief-of-staff-model.md):
// the company switch Solo / Team with the department default level and its lock
// (`chiefOfStaff.mode` / `.setMode`, managers only), and each person's own card "Who may direct my
// agents" (`agentDirection.get` / `.put`, every member). The heads are the people other than the
// CEO who supervise a DE of the twin; Solo with heads suggests Team (never switched here).
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { json, notFound, problem, validation } from "./fake";
import type { FakeDe } from "./fake-data";

type ModeView = OperationOutput<"chiefOfStaff.mode">;
type DirectionView = OperationOutput<"agentDirection.get">;
type Setting = DirectionView["setting"];
type Level = Setting["level"];

/** The agents-gated routes of this twin (`feature: agents`). */
export const FAKE_COS_MODES_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "chiefOfStaff.mode",
  "chiefOfStaff.setMode",
  "agentDirection.get",
  "agentDirection.put",
]);

const LEVELS: readonly Level[] = ["hands_off", "inform", "ask"];
const DEFAULT_SETTING = (level: Level): Setting => ({
  level,
  ask_for: [],
  timeout_hours: 4,
  fallback: "proceed",
});

type Company = { mode: ModeView["mode"]; department_level_default: Level; locked: boolean };

export function createCosModesFake(deps: {
  desOf: (companyId: string) => FakeDe[];
  /** The CEO's membership (the company owner). */
  ceoOf: (companyId: string) => string | null;
  peopleOf: (companyId: string) => { membership_id: string; name: string }[];
}) {
  const companies = new Map<string, Company>();
  const settings = new Map<string, Setting>();

  const companyOf = (co: string): Company =>
    companies.get(co) ?? { mode: "solo", department_level_default: "inform", locked: false };
  const personOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;
  const nameOf = (co: string, id: string) =>
    deps.peopleOf(co).find((x) => x.membership_id === id)?.name ??
    deps.desOf(co).find((d) => d.supervisor.membership_id === id)?.supervisor.name ??
    "Someone";
  /** The setting as it applies (the company default where the person set none; the lock wins). */
  const applied = (co: string, membership: string): Setting => {
    const c = companyOf(co);
    const own = settings.get(membership) ?? DEFAULT_SETTING(c.department_level_default);
    return c.locked ? { ...own, level: c.department_level_default } : own;
  };
  const agentsOf = (co: string, membership: string) =>
    deps.desOf(co).filter((d) => !d.removed_at && d.supervisor.membership_id === membership).length;

  function modeView(co: string): ModeView {
    const c = companyOf(co);
    const ceo = deps.ceoOf(co);
    const counts = new Map<string, number>();
    for (const d of deps.desOf(co)) {
      if (d.removed_at || d.supervisor.membership_id === ceo) continue;
      counts.set(d.supervisor.membership_id, (counts.get(d.supervisor.membership_id) ?? 0) + 1);
    }
    const heads = [...counts].map(([membership_id, agents]) => ({
      membership_id,
      name: nameOf(co, membership_id),
      agents,
      level: applied(co, membership_id).level,
    }));
    return {
      ...c,
      ceo: ceo ? { membership_id: ceo, name: nameOf(co, ceo) } : null,
      heads,
      suggest_team: c.mode === "solo" && heads.length > 0,
    };
  }

  function directionView(co: string, membership: string): DirectionView {
    const c = companyOf(co);
    return {
      mode: c.mode,
      setting: applied(co, membership),
      locked: c.locked,
      company_default: c.department_level_default,
      agents: agentsOf(co, membership),
    };
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "chiefOfStaff.mode": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json(modeView(principal.company.id));
    },
    "chiefOfStaff.setMode": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Partial<Company>;
      if (b.mode !== "solo" && b.mode !== "team") return validation("mode: solo or team");
      if (!LEVELS.includes(b.department_level_default as Level))
        return validation("department_level_default: hands_off, inform or ask");
      if (typeof b.locked !== "boolean") return validation("locked: a boolean");
      companies.set(principal.company.id, {
        mode: b.mode,
        department_level_default: b.department_level_default as Level,
        locked: b.locked,
      });
      return json(modeView(principal.company.id));
    },
    "agentDirection.get": ({ principal }) => {
      if (!principal) return notFound();
      const me = personOf(principal);
      if (!me) return problem(403, "forbidden", "Forbidden");
      return json(directionView(principal.company.id, me.membership_id));
    },
    "agentDirection.put": ({ principal, body }) => {
      if (!principal) return notFound();
      const me = personOf(principal);
      if (!me) return problem(403, "forbidden", "Forbidden");
      const co = principal.company.id;
      if (companyOf(co).locked)
        return problem(403, "forbidden", "Forbidden", "The company has locked the level");
      const b = (body ?? {}) as Partial<Setting>;
      if (!LEVELS.includes(b.level as Level)) return validation("level: hands_off, inform or ask");
      const askFor = Array.isArray(b.ask_for) ? b.ask_for : null;
      if (!askFor || askFor.length > 2 || askFor.some((a) => a !== "assign" && a !== "priority"))
        return validation("ask_for: assign, priority");
      const hours = b.timeout_hours;
      if (typeof hours !== "number" || !Number.isInteger(hours) || hours < 1 || hours > 24)
        return validation("timeout_hours: between 1 and 24");
      if (b.fallback !== "proceed" && b.fallback !== "escalate")
        return validation("fallback: proceed or escalate");
      settings.set(me.membership_id, {
        level: b.level as Level,
        ask_for: [...new Set(askFor)],
        timeout_hours: hours,
        fallback: b.fallback,
      });
      return json(directionView(co, me.membership_id));
    },
  };

  return { handlers };
}
